/** Complete low-frequency screener controls shown inside the advanced disclosure. */

import { formatExchangeLabel } from "../../lib/format";
import type { ScreenerFilterState } from "../../lib/types";
import { Input } from "../ui/input";
import { FILTER_CONTROL_CONFIG, RELATIONSHIP_OPTIONS } from "./filter-control-config";
import {
  DateRange,
  FilterSection,
  NumberRange,
  OptionButtons,
  standaloneFilterButtonClass,
  toggleFilterValue
} from "./filter-fields";

interface AdvancedFilterControlsProps {
  filters: ScreenerFilterState;
  exchangeOptions: string[];
  onFilterPatch: (filters: Partial<ScreenerFilterState>) => void;
}

/** Renders every advanced field from the current ScreenerFilterState contract. */
export function AdvancedFilterControls({
  filters,
  exchangeOptions,
  onFilterPatch
}: AdvancedFilterControlsProps) {
  return (
    <div className="space-y-3">
      <DateRange
        label="Trade date"
        from={filters.tradeDateFrom}
        to={filters.tradeDateTo}
        onFromChange={(tradeDateFrom) => onFilterPatch({ tradeDateFrom })}
        onToChange={(tradeDateTo) => onFilterPatch({ tradeDateTo })}
      />

      <FilterSection label="Cluster minimum">
        <button
          type="button"
          aria-pressed={filters.minDistinctInsiders === 2}
          className={standaloneFilterButtonClass(filters.minDistinctInsiders === 2)}
          onClick={() =>
            onFilterPatch({ minDistinctInsiders: filters.minDistinctInsiders === 2 ? null : 2 })
          }
        >
          2+ insiders
        </button>
      </FilterSection>

      <FilterSection label="Exchange">
        <div className="flex flex-wrap gap-1">
          {exchangeOptions.map((exchange) => {
            const active = filters.exchanges.includes(exchange);
            return (
              <button
                key={exchange}
                type="button"
                aria-pressed={active}
                className={standaloneFilterButtonClass(active)}
                onClick={() =>
                  onFilterPatch({ exchanges: toggleFilterValue(filters.exchanges, exchange) })
                }
              >
                {formatExchangeLabel(exchange)}
              </button>
            );
          })}
        </div>
      </FilterSection>

      <FilterSection label="Activity">
        <OptionButtons
          options={FILTER_CONTROL_CONFIG.activities}
          selected={filters.transactionActivities}
          onChange={(transactionActivities) => onFilterPatch({ transactionActivities })}
        />
        {filters.transactionActivities.length > 0 ? (
          <button
            type="button"
            aria-label="Clear transaction activity filter"
            className="text-xs font-medium text-muted-foreground transition-colors duration-150 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            onClick={() => onFilterPatch({ transactionActivities: [] })}
          >
            Clear
          </button>
        ) : null}
      </FilterSection>

      <FilterSection label="Transaction code">
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Activity codes</span>
          <Input
            aria-label="Transaction codes"
            className="h-9 px-2 text-sm"
            placeholder="P, S, A"
            value={filters.transactionCodes.join(", ")}
            onChange={(event) =>
              onFilterPatch({
                transactionCodes: event.target.value
                  .split(",")
                  .map((value) => value.trim().toUpperCase())
                  .filter(Boolean)
              })
            }
          />
        </label>
      </FilterSection>

      <FilterSection label="Security status">
        <OptionButtons
          options={FILTER_CONTROL_CONFIG.securityStatuses}
          selected={filters.securityStatuses}
          onChange={(securityStatuses) => onFilterPatch({ securityStatuses })}
        />
      </FilterSection>

      <NumberRange
        label="Shares"
        minimum={filters.minShares}
        maximum={filters.maxShares}
        onMinimumChange={(minShares) => onFilterPatch({ minShares })}
        onMaximumChange={(maxShares) => onFilterPatch({ maxShares })}
      />
      <NumberRange
        label="Price"
        minimum={filters.minPrice}
        maximum={filters.maxPrice}
        onMinimumChange={(minPrice) => onFilterPatch({ minPrice })}
        onMaximumChange={(maxPrice) => onFilterPatch({ maxPrice })}
      />
      <NumberRange
        label="Holdings change"
        minimum={filters.minHoldingsChange}
        maximum={filters.maxHoldingsChange}
        onMinimumChange={(minHoldingsChange) => onFilterPatch({ minHoldingsChange })}
        onMaximumChange={(maxHoldingsChange) => onFilterPatch({ maxHoldingsChange })}
      />

      <FilterSection label="Confidence">
        <OptionButtons
          options={FILTER_CONTROL_CONFIG.confidences}
          selected={filters.confidences}
          onChange={(confidences) => onFilterPatch({ confidences })}
        />
      </FilterSection>
      <FilterSection label="Amendments">
        <OptionButtons
          options={FILTER_CONTROL_CONFIG.amendments}
          selected={filters.amendmentStates}
          onChange={(amendmentStates) => onFilterPatch({ amendmentStates })}
        />
      </FilterSection>
      <FilterSection label="Value quality">
        <OptionButtons
          options={FILTER_CONTROL_CONFIG.valueQualities}
          selected={filters.valueQualities}
          onChange={(valueQualities) => onFilterPatch({ valueQualities })}
        />
      </FilterSection>
      <FilterSection label="Missing fields">
        <OptionButtons
          options={FILTER_CONTROL_CONFIG.missingFields}
          selected={filters.missingFieldFlags}
          onChange={(missingFieldFlags) => onFilterPatch({ missingFieldFlags })}
        />
      </FilterSection>

      <FilterSection label="Industry">
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Industry description</span>
          <Input
            className="h-9 px-2 text-sm"
            value={filters.industryDescription}
            onChange={(event) => onFilterPatch({ industryDescription: event.target.value })}
          />
        </label>
      </FilterSection>

      <NumberRange
        label="Filing lag"
        minimum={filters.minFilingLagDays}
        maximum={filters.maxFilingLagDays}
        onMinimumChange={(minFilingLagDays) => onFilterPatch({ minFilingLagDays })}
        onMaximumChange={(maxFilingLagDays) => onFilterPatch({ maxFilingLagDays })}
      />

      <FilterSection label="Accession">
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Accession number</span>
          <Input
            className="h-9 px-2 font-mono text-sm"
            value={filters.accessionNumber}
            onChange={(event) => onFilterPatch({ accessionNumber: event.target.value })}
          />
        </label>
      </FilterSection>

      <FilterSection label="Plan status">
        <OptionButtons
          options={FILTER_CONTROL_CONFIG.plans}
          selected={filters.planStatuses}
          onChange={(planStatuses) => onFilterPatch({ planStatuses })}
        />
      </FilterSection>

      <FilterSection label="Insider role">
        <OptionButtons
          options={RELATIONSHIP_OPTIONS}
          selected={filters.relationshipFlags}
          onChange={(relationshipFlags) => onFilterPatch({ relationshipFlags })}
        />
      </FilterSection>

      <FilterSection label="Favorites">
        <button
          type="button"
          aria-pressed={filters.favoritesOnly}
          className={standaloneFilterButtonClass(filters.favoritesOnly)}
          onClick={() => onFilterPatch({ favoritesOnly: !filters.favoritesOnly })}
        >
          Favorites only
        </button>
      </FilterSection>

      <FilterSection label="Market cap">
        <OptionButtons
          options={FILTER_CONTROL_CONFIG.marketCaps}
          selected={filters.marketCapBuckets}
          onChange={(marketCapBuckets) => onFilterPatch({ marketCapBuckets })}
        />
      </FilterSection>

      <FilterSection label="Materiality">
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Minimum value to market cap (bps)</span>
          <Input
            type="number"
            className="h-9 px-2 text-sm"
            value={filters.minValueToMarketCapBps ?? ""}
            onChange={(event) => {
              const value = event.target.value.trim();
              const parsed = value === "" ? null : Number(value);
              onFilterPatch({
                minValueToMarketCapBps: parsed === null || Number.isFinite(parsed) ? parsed : null
              });
            }}
          />
        </label>
      </FilterSection>
    </div>
  );
}
